/**
 * Filename: SecurityThreatReport.tsx
 * Description: Live persisted security incident feed and permission-gated investigation workflow.
 * Created by: Teddy Gil "Shin" Ravis Orbeta
 * Created: 2026-08-26
 * Updated: 2026-08-27
 */

import React, { useState, useEffect, useCallback } from 'react';

export interface SecurityIncident {
  id: string;
  timestamp: string;
  type: 'OTP_RATE_LIMIT_1H' | 'OTP_RATE_LIMIT_24H' | 'OTP_RATE_LIMIT_7D' | 'UNAUTHORIZED_API_ACCESS' | 'SUSPICIOUS_IP_BURST' | 'DATA_LEAK_PREVENTION_ALERT';
  target: string;
  severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  status: 'BLOCKED' | 'INVESTIGATING' | 'RESOLVED';
  actionTaken: string;
  ipAddress: string;
}

interface SecurityReportResponse {
  incidents?: SecurityIncident[];
  systemStatus?: string;
  metrics?: {
    otpBlocks24h: number;
    unauthorizedApiAttempts24h: number;
    encryptionStatus: string;
    wafStatus: string;
  };
}

function getDynamicSecurityAdvisories(incidents: SecurityIncident[]) {
  const advisories: { title: string; icon: string; text: string; action: string }[] = [];

  const hasOtpSpam = incidents.some((i) => i.type.startsWith('OTP_RATE_LIMIT'));
  const hasIpBurst = incidents.some((i) => i.type === 'SUSPICIOUS_IP_BURST');
  const hasUnauthorized = incidents.some((i) => i.type === 'UNAUTHORIZED_API_ACCESS');
  const hasDataLeak = incidents.some((i) => i.type === 'DATA_LEAK_PREVENTION_ALERT');
  const hasCritical = incidents.some((i) => i.severity === 'CRITICAL');

  if (hasOtpSpam) {
    const otpIncidents = incidents.filter((i) => i.type.startsWith('OTP_RATE_LIMIT'));
    const targets = Array.from(new Set(otpIncidents.map((i) => i.target))).join(', ');
    advisories.push({
      title: 'SMS OTP Dispatch Rate Limit Active',
      icon: '📱',
      text: `High frequency of SMS verification requests detected for target(s): ${targets}. Automatic rate limits are currently blocking unauthorized OTP dispatch.`,
      action: 'Action: Inspect SNS SMS logs and consider blocking abusing numbers.',
    });
  }

  if (hasIpBurst) {
    const ipIncidents = incidents.filter((i) => i.type === 'SUSPICIOUS_IP_BURST');
    const ips = Array.from(new Set(ipIncidents.map((i) => i.ipAddress))).join(', ');
    advisories.push({
      title: 'Suspicious IP Traffic Burst',
      icon: '🌐',
      text: `A persisted suspicious-traffic incident was recorded for IP address(es): ${ips}.`,
      action: 'Action: Investigate the source and verify edge-protection configuration before changing deny rules.',
    });
  }

  if (hasUnauthorized) {
    advisories.push({
      title: 'Unauthorized Endpoint Access Attempts',
      icon: '🔒',
      text: 'Persisted unauthorized endpoint access attempts require review against API Gateway access logs.',
      action: 'Action: Correlate request IDs and verify the rejection outcome in authoritative gateway logs.',
    });
  }

  if (hasDataLeak || hasCritical) {
    advisories.push({
      title: 'Data Leak & Admin Privilege Escalation Alert',
      icon: '🚨',
      text: 'A critical persisted security incident requires administrator review.',
      action: 'Action: Audit AWS IAM access keys and enforce MFA for administrator accounts.',
    });
  }

  if (advisories.length === 0) {
    advisories.push({
      title: 'Zero Active Threats in Selected Filter',
      icon: '✅',
      text: 'No persisted incidents match the selected filter. This does not prove that every provider control is healthy.',
      action: 'Action: Continue monitoring the live feed and system operations status.',
    });
  }

  return advisories;
}

const getIntervalLabel = (ms: number) => {
  switch (ms) {
    case 10000:
      return '🟢 LIVE FEED (Every 10s)';
    case 60000:
      return '⏱️ AUTO-REFRESH (Every 1m)';
    case 300000:
      return '⏱️ AUTO-REFRESH (Every 5m)';
    case 0:
      return '⏸️ PAUSED (Manual Only)';
    default:
      return '⏱️ AUTO-REFRESH';
  }
};

export function SecurityThreatReport({
  api,
  canInvestigate,
  canResolve,
}: {
  api: (path: string, options?: RequestInit) => Promise<unknown>;
  canInvestigate: boolean;
  canResolve: boolean;
}) {
  const [incidents, setIncidents] = useState<SecurityIncident[]>([]);
  const [lastUpdated, setLastUpdated] = useState<string>(new Date().toLocaleTimeString('ja-JP'));
  const [refreshIntervalMs, setRefreshIntervalMs] = useState<number>(10000);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [filterSeverity, setFilterSeverity] = useState<string>('ALL');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [reportStatus, setReportStatus] = useState('UNKNOWN');
  const [feedError, setFeedError] = useState('');
  const [metrics, setMetrics] = useState({ otpBlocks24h: 0, unauthorizedApiAttempts24h: 0, encryptionStatus: 'UNKNOWN', wafStatus: 'UNKNOWN' });

  // Action Modal State
  const [modalIncident, setModalIncident] = useState<SecurityIncident | null>(null);
  const [modalActionType, setModalActionType] = useState<'INVESTIGATING' | 'RESOLVED' | null>(null);
  const [actionReason, setActionReason] = useState<string>('');
  const [actionNotes, setActionNotes] = useState<string>('');
  const [isSubmittingAction, setIsSubmittingAction] = useState<boolean>(false);
  const [actionError, setActionError] = useState<string>('');

  const fetchLiveReportData = useCallback(async () => {
    setIsRefreshing(true);
    setFeedError('');
    try {
      const data = (await api('/admin/security-threats')) as SecurityReportResponse;
      if (data && Array.isArray(data.incidents)) {
        setIncidents(data.incidents);
        setReportStatus(data.systemStatus ?? 'UNKNOWN');
        if (data.metrics) setMetrics(data.metrics);
      }
      setLastUpdated(new Date().toLocaleTimeString('ja-JP'));
    } catch (err) {
      setFeedError(err instanceof Error ? err.message : 'The security feed is unavailable.');
    } finally {
      setIsRefreshing(false);
    }
  }, [api]);

  useEffect(() => {
    fetchLiveReportData();
    if (refreshIntervalMs <= 0) return;
    const timer = setInterval(() => {
      fetchLiveReportData();
    }, refreshIntervalMs);
    return () => clearInterval(timer);
  }, [fetchLiveReportData, refreshIntervalMs]);

  const handleExecuteAction = async () => {
    if (!modalIncident || !modalActionType) return;
    if (!actionReason.trim()) {
      setActionError('Justification reason is required for security audit trail.');
      return;
    }

    setIsSubmittingAction(true);
    setActionError('');
    try {
      await api('/admin/security-threats/action', {
        method: 'POST',
        body: JSON.stringify({
          incidentId: modalIncident.id,
          status: modalActionType,
          actionTaken: actionNotes || `Status updated to ${modalActionType} by admin`,
          reason: actionReason,
        }),
      });
      setModalIncident(null);
      setModalActionType(null);
      setActionReason('');
      setActionNotes('');
      await fetchLiveReportData();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Action failed.');
    } finally {
      setIsSubmittingAction(false);
    }
  };

  const filteredIncidents = incidents.filter((item) => {
    const matchesSeverity = filterSeverity === 'ALL' || item.severity === filterSeverity;
    const matchesSearch =
      item.target.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.type.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.ipAddress.includes(searchQuery) ||
      item.id.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSeverity && matchesSearch;
  });

  const exportReportJson = () => {
    const dataStr = 'data:text/json;charset=utf-8,' + encodeURIComponent(JSON.stringify(filteredIncidents, null, 2));
    const downloadAnchor = document.createElement('a');
    downloadAnchor.setAttribute('href', dataStr);
    downloadAnchor.setAttribute('download', `ShinQ_Security_Incident_Report_${new Date().toISOString().slice(0, 10)}.json`);
    document.body.appendChild(downloadAnchor);
    downloadAnchor.click();
    downloadAnchor.remove();
  };

  const getSeverityBadge = (severity: SecurityIncident['severity']) => {
    switch (severity) {
      case 'CRITICAL':
        return <span style={{ backgroundColor: '#FEE2E2', color: '#991B1B', padding: '4px 8px', borderRadius: '6px', fontWeight: 'bold', fontSize: '12px' }}>🔴 CRITICAL</span>;
      case 'HIGH':
        return <span style={{ backgroundColor: '#FFEDD5', color: '#9A3412', padding: '4px 8px', borderRadius: '6px', fontWeight: 'bold', fontSize: '12px' }}>🟠 HIGH</span>;
      case 'MEDIUM':
        return <span style={{ backgroundColor: '#FEF3C7', color: '#92400E', padding: '4px 8px', borderRadius: '6px', fontWeight: 'bold', fontSize: '12px' }}>🟡 MEDIUM</span>;
      case 'LOW':
        return <span style={{ backgroundColor: '#E0E7FF', color: '#3730A3', padding: '4px 8px', borderRadius: '6px', fontWeight: 'bold', fontSize: '12px' }}>🔵 LOW</span>;
    }
  };

  const otpBlocksCount = metrics.otpBlocks24h;
  const unauthorizedCount = metrics.unauthorizedApiAttempts24h;
  const dynamicAdvisories = getDynamicSecurityAdvisories(filteredIncidents);

  return (
    <div className="security-console">
      {/* Header Banner */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <h1 className="security-header-title">🛡️ Security & Threat Report Console</h1>
            <span style={{ fontSize: '12px', padding: '3px 10px', borderRadius: '12px', backgroundColor: refreshIntervalMs === 10000 ? '#DEF7EC' : refreshIntervalMs === 0 ? '#F3F4F6' : '#FEF3C7', color: refreshIntervalMs === 10000 ? '#03543F' : refreshIntervalMs === 0 ? '#374151' : '#92400E', fontWeight: 'bold' }}>
              {getIntervalLabel(refreshIntervalMs)}
            </span>
          </div>
          <p className="security-header-subtitle">
            Real-time security analytics, rate limit breaches, potential data leak monitoring, and threat prevention.
            <span style={{ marginLeft: '12px', fontSize: '12px', opacity: 0.8 }}>Last synced: <strong>{lastUpdated}</strong></span>
          </p>
        </div>

        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ fontSize: '13px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span>Fetch Interval:</span>
            <select
              value={refreshIntervalMs}
              onChange={(e) => setRefreshIntervalMs(Number(e.target.value))}
              style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--line)', fontSize: '13px', fontWeight: '600' }}
            >
              <option value={10000}>🟢 Live Feed (Every 10 Seconds) [Default]</option>
              <option value={60000}>⏱️ Every 1 Minute</option>
              <option value={300000}>⏱️ Every 5 Minutes</option>
              <option value={0}>⏸️ Manual Refresh Only</option>
            </select>
          </label>

          <button
            onClick={() => fetchLiveReportData()}
            disabled={isRefreshing}
            style={{ backgroundColor: isRefreshing ? '#9CA3AF' : '#10B981', color: '#FFFFFF', padding: '9px 16px', borderRadius: '8px', border: 'none', fontWeight: '600', cursor: isRefreshing ? 'not-allowed' : 'pointer', fontSize: '13px' }}
          >
            {isRefreshing ? '🔄 Syncing...' : '🔄 Refresh Live Feed'}
          </button>
          <button
            onClick={exportReportJson}
            style={{ backgroundColor: '#4F46E5', color: '#FFFFFF', padding: '9px 18px', borderRadius: '8px', border: 'none', fontWeight: '600', cursor: 'pointer', fontSize: '13px' }}
          >
            📥 Export Report (JSON)
          </button>
        </div>
      </div>

      {/* Security Status Cards */}
      {feedError && <div className="alert error">{feedError}</div>}
      <div className="security-card-grid">
        <div className="security-stat-card security-stat-normal">
          <div style={{ fontSize: '13px', fontWeight: '600' }}>SYSTEM SECURITY STATUS</div>
          <div style={{ fontSize: '24px', fontWeight: '800', marginTop: '4px' }}>{reportStatus}</div>
          <div style={{ fontSize: '12px', marginTop: '4px', opacity: 0.9 }}>Feed-derived status only</div>
        </div>

        <div className="security-stat-card security-stat-warning">
          <div style={{ fontSize: '13px', fontWeight: '600' }}>OTP RATE LIMIT BLOCKS (24H)</div>
          <div style={{ fontSize: '24px', fontWeight: '800', marginTop: '4px' }}>{otpBlocksCount} INCIDENTS</div>
          <div style={{ fontSize: '12px', marginTop: '4px', opacity: 0.9 }}>Hourly / Daily Limit Enforced</div>
        </div>

        <div className="security-stat-card security-stat-info">
          <div style={{ fontSize: '13px', fontWeight: '600' }}>UNAUTHORIZED API ATTEMPTS</div>
          <div style={{ fontSize: '24px', fontWeight: '800', marginTop: '4px' }}>{unauthorizedCount} BLOCKED</div>
          <div style={{ fontSize: '12px', marginTop: '4px', opacity: 0.9 }}>Persisted incidents in last 24 hours</div>
        </div>

        <div className="security-stat-card security-stat-neutral">
          <div style={{ fontSize: '13px', fontWeight: '600', opacity: 0.8 }}>DATA ENCRYPTION AT REST</div>
          <div style={{ fontSize: '18px', fontWeight: '800', marginTop: '4px' }}>{metrics.encryptionStatus}</div>
          <div style={{ fontSize: '12px', marginTop: '4px', opacity: 0.7 }}>WAF: {metrics.wafStatus}</div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="security-filter-box">
        <div style={{ flex: 1, minWidth: '200px' }}>
          <input
            type="text"
            placeholder="Search by IP, target, or incident ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid var(--line)', fontSize: '14px' }}
          />
        </div>
        <div>
          <select
            value={filterSeverity}
            onChange={(e) => setFilterSeverity(e.target.value)}
            style={{ padding: '10px 14px', borderRadius: '8px', border: '1px solid var(--line)', fontSize: '14px' }}
          >
            <option value="ALL">All Severity Levels</option>
            <option value="CRITICAL">🔴 Critical Only</option>
            <option value="HIGH">🟠 High Only</option>
            <option value="MEDIUM">🟡 Medium Only</option>
            <option value="LOW">🔵 Low Only</option>
          </select>
        </div>
      </div>

      {/* Incident Table */}
      <div className="security-table-container">
        <table className="security-table">
          <thead>
            <tr>
              <th>Incident ID</th>
              <th>Timestamp (JST)</th>
              <th>Threat Event Type</th>
              <th>Target / Vector</th>
              <th>IP Address</th>
              <th>Severity</th>
              <th>Action Taken</th>
              <th>Admin Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredIncidents.length === 0 ? (
              <tr>
                <td colSpan={8} style={{ padding: '24px', textAlign: 'center', color: 'var(--muted)' }}>
                  No security threats or incidents matching your filter criteria.
                </td>
              </tr>
            ) : (
              filteredIncidents.map((incident) => (
                <tr key={incident.id}>
                  <td style={{ fontFamily: 'monospace', fontWeight: 'bold', color: '#4F46E5' }}>{incident.id}</td>
                  <td>{incident.timestamp}</td>
                  <td style={{ fontWeight: '600' }}>{incident.type}</td>
                  <td>{incident.target}</td>
                  <td style={{ fontFamily: 'monospace', opacity: 0.8 }}>{incident.ipAddress}</td>
                  <td>{getSeverityBadge(incident.severity)}</td>
                  <td style={{ color: '#059669', fontWeight: '500', fontSize: '13px' }}>{incident.actionTaken}</td>
                  <td>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      {incident.status !== 'RESOLVED' && canInvestigate && incident.status !== 'INVESTIGATING' && (
                        <button
                          className="secondary"
                          style={{ padding: '4px 8px', fontSize: '12px' }}
                          onClick={() => {
                            setModalIncident(incident);
                            setModalActionType('INVESTIGATING');
                            setActionReason('');
                            setActionNotes('');
                            setActionError('');
                          }}
                        >
                          Investigate
                        </button>
                      )}
                      {incident.status !== 'RESOLVED' && canResolve ? (
                        <button
                          className="secondary"
                          style={{ padding: '4px 8px', fontSize: '12px' }}
                          onClick={() => {
                            setModalIncident(incident);
                            setModalActionType('RESOLVED');
                            setActionReason('');
                            setActionNotes('');
                            setActionError('');
                          }}
                        >
                          Resolve
                        </button>
                      ) : incident.status === 'RESOLVED' ? (
                        <span style={{ fontSize: '12px', color: '#059669', fontWeight: '600' }}>✓ Resolved</span>
                      ) : null}

                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Dynamic Advisory & Actionable Prevention Section */}
      <div className="security-advisory-card">
        <h2 style={{ fontSize: '18px', fontWeight: '700', marginTop: 0, marginBottom: '16px' }}>
          💡 Dynamic Security Advisory & Prevention Recommendations
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '20px', fontSize: '14px' }}>
          {dynamicAdvisories.map((advisory, idx) => (
            <div key={idx} style={{ backgroundColor: 'rgba(0, 0, 0, 0.03)', padding: '16px', borderRadius: '10px', border: '1px solid var(--line)' }}>
              <h3 style={{ fontSize: '15px', fontWeight: '700', margin: '0 0 8px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>{advisory.icon}</span>
                <span>{advisory.title}</span>
              </h3>
              <p style={{ margin: '0 0 8px 0', opacity: 0.9, lineHeight: '1.5' }}>{advisory.text}</p>
              <div style={{ fontSize: '13px', fontWeight: '600', color: '#4F46E5', backgroundColor: 'rgba(79, 70, 229, 0.08)', padding: '8px 12px', borderRadius: '6px', marginTop: '8px' }}>
                {advisory.action}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Security Action Modal */}
      {modalIncident && modalActionType && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'var(--surface, #ffffff)', padding: '24px', borderRadius: '12px', maxWidth: '500px', width: '90%', boxShadow: '0 10px 25px rgba(0,0,0,0.2)' }}>
            <h2 style={{ marginTop: 0, fontSize: '18px' }}>
              Update Security Incident ({modalActionType})
            </h2>
            <p style={{ fontSize: '14px', opacity: 0.9 }}>
              Target Incident: <strong>{modalIncident.id}</strong> ({modalIncident.type})
            </p>

            {actionError && (
              <div style={{ padding: '10px', backgroundColor: '#FEE2E2', color: '#991B1B', borderRadius: '6px', fontSize: '13px', marginBottom: '12px' }}>
                {actionError}
              </div>
            )}

            <>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '8px' }}>
                  Mandatory Audit Reason (Justification):
                  <input
                    type="text"
                    placeholder="e.g. Verified legitimate IP burst / Resolved SNS alert #104"
                    value={actionReason}
                    onChange={(e) => setActionReason(e.target.value)}
                    style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid var(--line)', marginTop: '4px', fontSize: '14px' }}
                  />
                </label>

                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '16px' }}>
                    Action Notes (Optional):
                    <input
                      type="text"
                      placeholder="e.g. Blocked IP in AWS WAF and notified team"
                      value={actionNotes}
                      onChange={(e) => setActionNotes(e.target.value)}
                      style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid var(--line)', marginTop: '4px', fontSize: '14px' }}
                    />
                  </label>

                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '16px' }}>
                  <button
                    className="secondary"
                    onClick={() => {
                      setModalIncident(null);
                      setModalActionType(null);
                      setActionError('');
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    className="primary"
                    disabled={isSubmittingAction || !actionReason.trim()}
                    onClick={() => void handleExecuteAction()}
                  >
                    {isSubmittingAction ? 'Processing...' : 'Confirm Action'}
                  </button>
                </div>
            </>
          </div>
        </div>
      )}
    </div>
  );
}
